import React, { useState, useEffect } from "react"
import { Users, UserCheck, Zap, Clock, Timer, Repeat, CheckSquare, ArrowRight, ChevronDown } from "lucide-react"
import { Card, CardContent } from "../../../components/ui/card"
import { TiltCard } from "../../../components/ui/TiltCard"
import { computeEngagementOverview } from "../../../utils/engagementHelpers"

const RANGE_OPTIONS = [
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
]

const TrendBadge = ({ value }) => {
  if (value === null || value === undefined) return null
  const isUp = value >= 0
  return (
    <span className={`text-xs font-medium ${isUp ? "text-success" : "text-destructive"}`}>
      {isUp ? "+" : ""}
      {value}%
    </span>
  )
}

const EngagementOverviewSection = ({ orgId, onNavigateTab, showReportLink = true }) => {
  const [range, setRange] = useState("30d")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeEngagementOverview(orgId, range).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId, range])

  const metrics = data
    ? [
        { label: "Daily Active Learners", value: data?.dailyActiveLearners?.toLocaleString(), icon: Users, tab: "intelligence-learners" },
        { label: "Weekly Active Learners", value: data?.weeklyActiveLearners?.toLocaleString(), icon: UserCheck, tab: "intelligence-learners" },
        { label: "Sessions", value: data?.sessions?.value?.toLocaleString(), change: data?.sessions?.change, icon: Zap, tab: "engagement-activity" },
        { label: "Learning Hours", value: data?.hours?.value?.toLocaleString(), change: data?.hours?.change, icon: Clock, tab: "engagement-activity" },
        { label: "Avg. Session Duration", value: `${data?.avgSessionMinutes?.value} min`, icon: Timer, tab: "engagement-activity" },
        { label: "Returning Learners", value: data?.returningLearners?.value?.toLocaleString(), icon: Repeat, tab: "intelligence-learners" },
        { label: "Lessons Completed", value: data?.lessonsCompleted?.value?.toLocaleString(), change: data?.lessonsCompleted?.change, icon: CheckSquare, tab: "intelligence-courses" },
      ]
    : []

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-foreground">Engagement Overview</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="appearance-none text-sm bg-background border border-border rounded-md pl-2.5 pr-7 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {RANGE_OPTIONS?.map((opt) => (
                <option key={opt?.key} value={opt?.key}>
                  {opt?.label}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          </div>
          {showReportLink && (
            <button
              onClick={() => onNavigateTab?.("engagement-activity")}
              className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              See Full Report
              <ArrowRight className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 7 })?.map((_, i) => (
            <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {metrics?.map((metric) => {
            const Icon = metric.icon
            return (
              <TiltCard key={metric.label} onClick={() => onNavigateTab?.(metric?.tab)} tiltStrength={6}>
                <Card className="shadow-none h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Icon className="size-4" />
                      <span className="text-sm font-medium">{metric.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-semibold tracking-tight text-foreground">{metric.value}</p>
                      <TrendBadge value={metric?.change} />
                    </div>
                  </CardContent>
                </Card>
              </TiltCard>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default EngagementOverviewSection